/* eslint-disable camelcase */
import * as yup from "yup"
import React, { useCallback, useMemo, useRef, useState } from "react"
import _ from "lodash"
import moment from "moment"
import numeral from "numeral"
import { Appearance, TextInput, TouchableOpacity, ViewStyle, Dimensions } from "react-native"
import { Cycle, Farm } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormProps } from "../form"
import { Formik } from "formik"
import { Icon, View } from "native-base"
import { JalaIcon } from "../../jala-icon/jala-icon"
import { ModelField } from "../../field/model-field/model-field"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { Text } from "../../text/text"
import { color, nativeBaseTheme } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { FormControl } from "../../form-control/form-control"
import { WalkthroughElement } from "../../walkthrough-element/walkthrough-element"

const windowWidth = Dimensions.get('window').width;

const FORM_DROPDOWN: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

const TITLE: ViewStyle = {
  borderTopWidth: 1,
  borderColor: color.palette.background,
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 10
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4
}

const OTHERS: ViewStyle = {
  width: windowWidth,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingHorizontal: 16,
  // marginBottom: 14,
  paddingBottom: 14,
  marginTop: 14,
  paddingTop: 14,
  backgroundColor: color.palette.white
}

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

export interface PlanktonMultiMeasurementFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    farm_id?: number,
    pond_id?: number,
    cycle_id?: number,
    measured_date?: string,
    measured_time?: string,

    total_plankton?: number,
    ga?: number,
    bga?: number,
    dino?: number,
    diatom?: number,
    prozoo?: number,
    floc?: number,
    gba?: number,
    euglenophyta?: number,
    zoo?: number,
    haptyophyta?: number,
    gga?: number,
    yga?: number,
  }
}

/**
 * Describe your component here
 */
export const PlanktonMultiMeasurementForm = observer(function PlanktonMultiMeasurementForm(props: PlanktonMultiMeasurementFormProps) {
  const {
    initialValues: initialValuesProps = {},
    onSubmit: onSubmitProps,
    colorScheme = Appearance.getColorScheme(),
    innerRef,
    parentScrollViewRef: scrollViewRef,
    ...rest
  } = props

  const [ isHide, setHide ] = useState<boolean>(true)

  const input = {
    // farm_id: useRef<TextInput>(),
    // cycle_id: useRef<TextInput>(),
    // measured_date: useRef<TextInput>(),
    // measured_time: useRef<TextInput>(),

    total_plankton: useRef<TextInput>(),
    ga: useRef<TextInput>(),
    bga: useRef<TextInput>(),
    dino: useRef<TextInput>(),
    diatom: useRef<TextInput>(),
    prozoo: useRef<TextInput>(),
    floc: useRef<TextInput>(),
    gba: useRef<TextInput>(),
    euglenophyta: useRef<TextInput>(),
    zoo: useRef<TextInput>(),
    haptyophyta: useRef<TextInput>(),
    gga: useRef<TextInput>(),
    yga: useRef<TextInput>(),
  }

  const nextInput = {
    // farm_id: () => input.cycle_id.current && input.cycle_id.current.focus(),
    // cycle_id: () => input.measured_date.current && input.measured_date.current.focus(),
    // measured_date: () => input.measured_time.current && input.measured_time.current.focus(),
    // measured_time: () => input.total_plankton.current && input.total_plankton.current.focus(),

    total_plankton: () => input.ga.current && input.ga.current.focus(),
    ga: () => input.bga.current && input.bga.current.focus(),
    bga: () => input.dino.current && input.dino.current.focus(),
    dino: () => input.diatom.current && input.diatom.current.focus(),
    diatom: () => input.prozoo.current && input.prozoo.current.focus(),
    prozoo: () => input.floc.current && input.floc.current.focus(),
    floc: () => input.gba.current && input.gba.current.focus(),
    gba: () => input.euglenophyta.current && input.euglenophyta.current.focus(),
    euglenophyta: () => input.zoo.current && input.zoo.current.focus(),
    zoo: () => input.haptyophyta.current && input.haptyophyta.current.focus(),
    haptyophyta: () => input.gga.current && input.gga.current.focus(),
    gga: () => input.yga.current && input.yga.current.focus(),
    yga: null,
  }

  const validationSchema = yup.object({
    farm_id: yup.number().nullable().required().label(translate("planktonMultiMeasurementForm.farmId")),
    cycle_id: yup.number().nullable().required().label(translate("planktonMultiMeasurementForm.cycleId")),
    measured_date: yup.mixed().nullable().required().label(translate("planktonMultiMeasurementForm.measuredDate")),
    measured_time: yup.mixed().nullable().required().label(translate("planktonMultiMeasurementForm.measuredTime")),

    // total_plankton: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.totalPlankton")),
    ga: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.ga")),
    bga: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.bga")),
    dino: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.dino")),
    diatom: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.diatom")),
    prozoo: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.prozoo")),
    floc: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.floc")),
    gba: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.gba")),
    euglenophyta: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.euglenophyta")),
    zoo: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.zoo")),
    haptyophyta: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.haptyophyta")),
    gga: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.gga")),
    yga: yup.number().min(0).nullable().label(translate("planktonMultiMeasurementForm.yga")),
  }).test(function (value) {
    const isValid = !![
      'total_plankton', 'ga', 'bga', 'dino', 'diatom', 'prozoo', 'floc', 'gba', 'euglenophyta', 'zoo', 'haptyophyta', 'gga', 'yga',
    ].find((k) => value[k])
    if (isValid) return true;
    else return this.createError({
      path: 'total_plankton|ga|bga|dino|diatom|prozoo|floc|gba|euglenophyta|zoo|haptyophyta|gga|yga',
      message: ''
    })
  })

  const planktonSum = useCallback((values: any) => {
    return _.sumBy([
      'ga', 'bga', 'dino', 'diatom', 'prozoo', 'floc', 'gba', 'euglenophyta', 'zoo', 'haptyophyta', 'gga', 'yga'
    ], (k) => values[k] || 0)
  }, [])

  const onSubmit = useCallback((values) => {
    onSubmitProps({
      ...values,
      total_plankton: planktonSum(values) || null,
      measured_at: values.measured_date
        && values.measured_time
        && moment(`${values.measured_date} ${values.measured_time}`).format('YYYY-MM-DDTHH:mm:ssZ')
        || null,
    })
  }, [onSubmitProps, planktonSum])

  const initialValues = useMemo<PlanktonMultiMeasurementFormProps["initialValues"]>(() => ({
    farm_id: null,
    pond_id: null,
    cycle_id: null,
    measured_date: moment().format('YYYY-MM-DD'),
    measured_time: moment().startOf('minutes').format('HH:mm:ss'),
    ..._.omit(initialValuesProps, 'measured_at')
  }), [initialValuesProps])

  const iconName = isHide ? "expand_more" : "expand_less"

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ values, initialValues, setFieldValue, setFieldTouched }) => (
        <>
          <ModelField
            name="farm_id"
            txPrefix="planktonMultiMeasurementForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Farm,
              isDisabled: !!initialValues.farm_id,
              onValueChange: async (value) => {
                await setFieldValue('cycle_id', null)
                await setFieldValue('pond_id', null)
                await setFieldValue('farm_id', value || null)
                await setFieldTouched('farm_id')
              }
            }}
          />
          <View style={TITLE}>
            <Text tx="planktonMultiMeasurementForm.plankton" preset="title3" colorScheme={colorScheme} />
          </View>
          <View style={BORDER} />
          <WalkthroughElement
            id="plankton-multi-measurement-form-first"
            scrollViewRef={scrollViewRef}
            scrollOffset={250}
          >
            <ModelField
              key={`${values.farm_id}-cycle_id`}
              name="cycle_id"
              txPrefix="planktonMultiMeasurementForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Cycle,
                dataLabel: "pond_name",
                fetchData: (model, search) => (
                  model.fetch({
                    farm_id__eq: values.farm_id,
                    sort: 'started_at,desc|pond_name_alpha_numeric'
                  }).then((models) => models.filter((m) => (
                    `${m.pond_name} - ${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
                  ).toLowerCase().includes((search || '').toLowerCase())))
                ),
                renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
                clearable: true,
                isDisabled: !values.farm_id || !!initialValues.cycle_id,
                onValueChange: async (value, index, item) => {
                  await setFieldValue('pond_id', (item || {}).pond_id || null)
                  await setFieldValue('cycle_id', value || null)
                  await setFieldTouched('cycle_id')
                },
                titleTx: "planktonMultiMeasurementForm.cycleIdPlaceholder"
              }}
            />
            <DateTimePickerField
              name="measured_date"
              txPrefix="planktonMultiMeasurementForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
            />
            <View flexDirection="row">
              <DateTimePickerField
                name="measured_time"
                txPrefix="planktonMultiMeasurementForm"
                colorScheme={colorScheme}
                formControlProps={{ flex: 1, isRequired: true }}
                dateTimePickerProps={{ dateTimePickerProps: { mode: "time" } }}
              />
              <View
                flex={1}
                style={FORM_DROPDOWN}
              ></View>
            </View>
            <NumeralField
              name="ga"
              txPrefix="planktonMultiMeasurementForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
            />
            <NumeralField
              name="bga"
              txPrefix="planktonMultiMeasurementForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
            />
            <FormControl
              labelTx="planktonMultiMeasurementForm.totalPlankton"
              colorScheme={colorScheme}
              inputProps={{
                testID: `jala:id/total_plankton-input`,
                placeholderTx: "planktonMultiMeasurementForm.totalPlanktonPlaceholder",
                value: numeral(planktonSum(values)).format('0,0.[00]'),
                style: {
                  borderWidth: 0
                }
              }}
              style={FORM_CONTROL}
            />
          </WalkthroughElement>
          <WalkthroughElement
            id="plankton-multi-measurement-second"
            scrollViewRef={scrollViewRef}
            scrollOffset={250}
          >
            <View style={OTHERS}>
              <Text tx="waterQualityMultiMeasurementForm.otherParameters" preset="title3" color={color.palette.gray800} />
              <TouchableOpacity onPress={() => setHide(!isHide)}>
                <Icon {...nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme })}
                      as={<JalaIcon name={iconName} />} size="24px" />
              </TouchableOpacity>
            </View>
          </WalkthroughElement>
          {!isHide &&
            <>
              <NumeralField
                name="dino"
                txPrefix="planktonMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="diatom"
                txPrefix="planktonMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="prozoo"
                txPrefix="planktonMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="floc"
                txPrefix="planktonMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="gba"
                txPrefix="planktonMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="euglenophyta"
                txPrefix="planktonMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="zoo"
                txPrefix="planktonMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="haptyophyta"
                txPrefix="planktonMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="gga"
                txPrefix="planktonMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
              <NumeralField
                name="yga"
                txPrefix="planktonMultiMeasurementForm"
                colorScheme={colorScheme}
                input={input}
                nextInput={nextInput} />
            </>
          }
        </>
      )}
    </Formik>
  )
})

PlanktonMultiMeasurementForm.displayName = 'PlanktonMultiMeasurementForm'
